import {Component, OnDestroy, OnInit} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {delay} from 'rxjs/operators';
import {UnitLengthMeasurementUpdated} from '../../../../core/linked-tables';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';

import {
    UnitLengthMeasurement,
    UnitLengthMeasurementOnServerCreated,
    selectUnitLengthMeasurementsActionLoading,
    selectUnitLengthMeasurementById,
    selectLastCreatedUnitLengthMeasurementId
} from '../../../../core/linked-tables';
import {Location} from '@angular/common';

@Component({
    selector: 'kt-unit-length-measurement-edit',
    templateUrl: './unit-length-measurement-edit.component.html',
    styleUrls: ['./unit-length-measurement-edit.component.scss'],
})


export class UnitLengthMeasurementEditComponent implements OnInit, OnDestroy {

    model: UnitLengthMeasurement;
    oldModel: UnitLengthMeasurement;

    unitLengthMeasurementForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    // Private properties
    private componentSubscriptions: Subscription;
    private subscriptions: Subscription[] = [];

    readonly = false

    /**
     * Component constructor
     *
     * @param subheaderService: SubheaderService
     * @param activatedRoute: ActivatedRoute
     * @param router: Router
     * @param fb: FormBuilder
     * @param location: Location
     * @param store: Store<AppState>
     */
    constructor(public subheaderService: SubheaderService,
                private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private location: Location,
                private store: Store<AppState>,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectUnitLengthMeasurementsActionLoading));
        const routeSubscription = combineLatest([
            this.activatedRoute.params,
            this.activatedRoute.data
        ]).subscribe(([params, data]) => {
            const id = params.id;
            this.readonly = data.readonly
            if (id && id > 0) {
                this.store.pipe(select(selectUnitLengthMeasurementById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initUnitLengthMeasurement();
                    }
                });
            } else {
                this.model = new UnitLengthMeasurement();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initUnitLengthMeasurement();
            }
        });
        this.subscriptions.push(routeSubscription);

        if (this.activatedRoute.snapshot.paramMap.get('id') != null) {
            this.setBreadCrumbs(Number(this.activatedRoute.snapshot.paramMap.get('id')));
        } else {
            this.setBreadCrumbs(0);
        }
    }

    setBreadCrumbs(id: number): void {
        this.subheaderService.setTitle(this.getComponentTitle());
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../admin-management`},
            {title: 'Linked tables', page: `../admin-management/linked-tables`},
            {title: 'Interaction types', page: `../admin-management/linked-tables/unit-measurements`},
            {
                title: this.getComponentTitle(),
                page: id > 0 ? `../admin-management/linked-tables/unit-measurements/edit/${id}`
                    : `../admin-management/linked-tables/unit-measurements/add`
            }
        ]);
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
    }

    initUnitLengthMeasurement() {
        this.createForm();
    }


    createForm() {
        this.unitLengthMeasurementForm = this.fb.group({
            name: [this.model.name, Validators.required],
            acronym: [this.model.acronym, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return this.translate.instant('UNIT_LENGTH_MEASUREMENT.FORM.TITLE.EDIT', {name: this.model.name});
        }
        return this.translate.instant('UNIT_LENGTH_MEASUREMENT.FORM.TITLE.NEW');
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.unitLengthMeasurementForm.controls[controlName];
        return control.invalid && control.touched;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.unitLengthMeasurementForm.markAsPristine();
        this.unitLengthMeasurementForm.markAsUntouched();
        this.unitLengthMeasurementForm.updateValueAndValidity();
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.unitLengthMeasurementForm.controls;
        /** check form */
        if (this.unitLengthMeasurementForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        if (this.model.id > 0) {
            this.updateUnitLengthMeasurement(this.model);
        } else {
            this.createUnitLengthMeasurement(this.model);
        }
    }

    /**
     * Update model
     *
     * @param _unitLengthMeasurement: UnitLengthMeasurementModel
     */
    updateUnitLengthMeasurement(_unitLengthMeasurement: UnitLengthMeasurement) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateUnitLengthMeasurement: Update<UnitLengthMeasurement> = {
            id: _unitLengthMeasurement.id,
            changes: _unitLengthMeasurement
        };
        this.store.dispatch(new UnitLengthMeasurementUpdated({
            partialunitLengthMeasurement: updateUnitLengthMeasurement,
            unitLengthMeasurement: _unitLengthMeasurement
        }));

        this.navigateToParent();
    }

    /**
     * Create model
     *
     * @param _unitLengthMeasurement: UnitLengthMeasurementModel
     */
    createUnitLengthMeasurement(_unitLengthMeasurement: UnitLengthMeasurement) {
        this.store.dispatch(new UnitLengthMeasurementOnServerCreated({unitLengthMeasurement: _unitLengthMeasurement}));
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedUnitLengthMeasurementId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model && this.model.name && this.model.name.length > 0
            && this.model.acronym && this.model.acronym.length > 0);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }

    canDeactivate() {
        if (this.discard()) {
            if (window.confirm(this.translate.instant('GENERAL.MESSAGE.LOST_CHANGES'))) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.name != this.oldModel.name || this.model.acronym != this.oldModel.acronym;
        }
        return false;
    }

}
